/**
 * The "Team" menu — reporting on the people a manager is responsible for. See
 * lib/navigation/nav-manifest.js for the entry shape and the vocabulary
 * `requires` draws from.
 */

/** @type {import("@/lib/navigation/nav-manifest").NavManifestItem[]} */
export const REPORT_ITEMS = [
  {
    // One page per person: their results, coaching report, assignments,
    // learning paths, tests and badges (app/workspaces/reports/members). The
    // one-rep coaching report that lived under Coaching is its tab now.
    href: "/workspaces/reports/members",
    label: "Members",
    description: "The people you're responsible for. Open one to see their work and coach them.",
    iconName: "IoPeopleOutline",
  },
  {
    href: "/workspaces/reports/manager-dashboard",
    label: "Manager Dashboard",
    description: "At-a-glance team performance and coaching priorities",
    iconName: "IoStatsChartOutline",
    requires: "supervisorOrAbove",
  },
  {
    href: "/workspaces/reports",
    label: "All Results",
    heading: "All results",
    description: "View and analyze results across all simulation types.",
    shortcut: "R",
    iconName: "IoBarChartOutline",
  },
  {
    label: "Practice",
    iconName: "LiaTheaterMasksSolid",
    subItems: [
      {
        href: "/workspaces/reports/discovery",
        label: "Discovery",
        iconName: "CiMap",
        requires: "reportModule",
      },
      {
        href: "/workspaces/reports/pitch",
        label: "Messaging / Pitch",
        iconName: "HiOutlinePresentationChartBar",
        requires: "reportModule",
      },
      {
        href: "/workspaces/reports/objections",
        label: "Objections",
        iconName: "HiNoSymbol",
        requires: "reportModule",
      },
      {
        href: "/workspaces/reports/cold-call",
        label: "Outbound & Inbound",
        iconName: "IoCallOutline",
        requires: "reportModule",
      },
      {
        href: "/workspaces/reports/plays",
        label: "Plays",
        iconName: "LiaTheaterMasksSolid",
        requires: "reportModule",
      },
      {
        href: "/workspaces/reports/management",
        label: "Management Practice",
        iconName: "HiOutlineUserGroup",
        requires: "reportModule",
      },
    ],
  },
  {
    label: "Execution",
    iconName: "IoFlashOutline",
    // Module Usage below carries no gate of its own, so this group decides
    // whether the studio does any execution work at all.
    requires: { any: ["dealClinic", "conversationAnalysis"] },
    subItems: [
      {
        href: "/workspaces/reports/transcript-results",
        label: "Conversation Analysis",
        description:
          "Every analyzed call across your team, scored and summarized.",
        iconName: "IoStatsChartOutline",
        requires: "reportModule",
      },
      {
        href: "/dem-cog/reports",
        label: "Deal Clinic",
        iconName: "IoBarChartOutline",
        requires: "dealClinic",
      },
      {
        href: "/workspaces/reports/execution",
        label: "Module Usage",
        heading: "Execution module usage",
        description:
          "Track how your team uses execution tools across the platform.",
        iconName: "IoFlashOutline",
      },
    ],
  },
  {
    label: "Engagement",
    iconName: "IoTimeOutline",
    subItems: [
      {
        // One report with a tab per period (week, month, quarter, a date
        // range); the menu opens it on the month.
        href: "/workspaces/reports/engagement/monthly",
        label: "Engagement",
        heading: "Engagement report",
        iconName: "IoTimeOutline",
      },
      {
        href: "/workspaces/admin/analytics",
        label: "Studio Usage",
        description: "Usage and activity across your studio or your team.",
        iconName: "IoBarChartOutline",
        requires: "supervisorOrAbove",
      },
      {
        href: "/workspaces/reports/enrollments",
        label: "LP Enrollments",
        heading: "Learning path enrollments",
        description:
          "Track who's enrolled in each learning path, where they are in it, and how much time they've spent.",
        iconName: "SlGraduation",
      },
      {
        href: "/workspaces/reports/overdue-items",
        label: "Overdue Items",
        heading: "Overdue items",
        description: "Items past their due date, grouped by user.",
        iconName: "CiCircleAlert",
      },
    ],
  },
  {
    label: "Tests",
    iconName: "IoDocumentTextOutline",
    subItems: [
      {
        href: "/workspaces/reports/tests/multiple-choice",
        label: "Multiple Choice",
        heading: "Multiple choice tests",
        description: "Review how your team performed on multiple choice tests.",
        iconName: "IoDocumentTextOutline",
      },
      {
        href: "/workspaces/reports/tests/faq",
        label: "Q&A Tests",
        heading: "Q&A tests",
        description: "Review how your team performed on Q&A tests.",
        iconName: "IoHelpCircleOutline",
      },
      {
        href: "/workspaces/reports/tests/glossary",
        label: "Glossary Tests",
        heading: "Glossary tests",
        description: "Review how your team performed on glossary tests.",
        iconName: "IoBookOutline",
      },
    ],
  },
  {
    label: "Coaching",
    iconName: "IoStatsChartOutline",
    subItems: [
      {
        href: "/workspaces/reports/team-coaching",
        label: "Team Coaching Report",
        description: "Aggregate performance insights across your team",
        iconName: "HiOutlineUserGroup",
      },
      {
        href: "/workspaces/reports/recordings",
        label: "Recordings",
        heading: "Recordings library",
        description: "Browse and review recorded practice sessions.",
        iconName: "IoDocumentTextOutline",
      },
      {
        href: "/workspaces/reports/feedback-comments",
        label: "Feedback Comments",
        heading: "Studio feedback",
        description: "Review feedback comments left across your studio.",
        iconName: "IoChatbubbleOutline",
      },
      {
        href: "/workspaces/reports/practice-sessions-export",
        label: "Sessions Export",
        heading: "Practice sessions export",
        description:
          "Export scored practice session data for analysis and reporting.",
        iconName: "IoDownloadOutline",
      },
    ],
  },
];
